import { IPairingPosition, IPosition } from "@vmosolutions/oc-interfaces";
import { Box, styled } from "@mui/material";
import { colorContrast } from "src/themes/util";

interface IPositionBadge {
  position?: IPosition | null;
  pairingPosition?: IPairingPosition;
  positionTitle?: string;
  count?: number;
  filled?: boolean;
  className?: string;
  error?: string;
  isInactive?: boolean;
  bgColor?: string;
}

const Badge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "filled" && prop !== "isInactive" && prop !== "error" && prop !== "bgColor",
})<{ filled?: boolean; isInactive?: boolean; error: string; bgColor?: string }>(({
  theme,
  filled,
  isInactive,
  error,
  bgColor,
}) => {
  const bg =
    bgColor !== undefined && bgColor !== ""
      ? bgColor
      : filled
        ? theme.palette.primary.main
        : theme.palette.common.white;
  return {
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    color: error
      ? theme.palette.common.white
      : bgColor
        ? colorContrast(bg, theme.palette.primary.dark, theme.palette.common.white)
        : filled
          ? theme.palette.primary.contrastText
          : theme.palette.primary.main,
    border: `1px solid ${theme.palette.background.default}`,
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    margin: "0px 1px 1px 0px",
    padding: 2,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "10px",
    transition: theme.transitions.create("color", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    background: isInactive
      ? `linear-gradient(to top left, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.primary.main} calc(50% - 2px), 
      ${theme.palette.error.main} 50%, 
      ${theme.palette.primary.main} calc(50% + 2px), 
      ${theme.palette.primary.main})`
      : undefined,
    backgroundColor: error ? theme.palette.error.main : bg,
  };
});

export const PositionBadge = ({
  position,
  pairingPosition,
  positionTitle,
  count,
  filled,
  className,
  error,
  isInactive,
  bgColor,
}: IPositionBadge): JSX.Element => {
  const crewCount = (pairingPosition || positionTitle) && count && count > 1 ? `x${count}` : "";

  const badgeContent = position
    ? position.title
    : pairingPosition?.position
      ? `${pairingPosition.position}${crewCount}`
      : positionTitle
        ? `${positionTitle}${crewCount}`
        : "None";

  const isFilled = filled && (pairingPosition?.crew_id || position || positionTitle) ? true : false;

  return (
    <Box
      className={`${className} ${error ? "error" : ""}`}
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        cursor: "default",
        userSelect: "none",
        height: "100%",
      }}
    >
      <Badge filled={isFilled} isInactive={isFilled && isInactive} bgColor={bgColor} error={error}>
        {badgeContent}
      </Badge>
    </Box>
  );
};
